import { useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Play } from "lucide-react";

export const getVideoEmbed = (url = "") => {
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
  if (yt) return { kind: "youtube", src: `https://www.youtube.com/embed/${yt[1]}` };
  const vimeo = url.match(/vimeo\.com\/(\d+)/);
  if (vimeo) return { kind: "vimeo", src: `https://player.vimeo.com/video/${vimeo[1]}` };
  if (/\.mp4(\?|$)/i.test(url)) return { kind: "mp4", src: url };
  return null;
};

const Lightbox = ({ items, index, onClose, onNavigate }) => {
  const item = index != null ? items[index] : null;

  const prev = useCallback(() => onNavigate((index - 1 + items.length) % items.length), [index, items.length, onNavigate]);
  const next = useCallback(() => onNavigate((index + 1) % items.length), [index, items.length, onNavigate]);

  useEffect(() => {
    if (index == null) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, onClose, prev, next]);

  const video = item?.media_type === "video" ? getVideoEmbed(item.url) : null;
  const title = item?.title || item?.titre || "";

  return (
    <AnimatePresence>
      {item && (
        <motion.div
          data-testid="gallery-lightbox"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[70] bg-[#050505]/95 backdrop-blur-md flex items-center justify-center p-4 md:p-12"
          onClick={onClose}
        >
          <button
            data-testid="lightbox-close"
            aria-label="Fermer"
            onClick={onClose}
            className="absolute top-6 right-6 w-12 h-12 flex items-center justify-center border border-white/20 text-white hover:border-gold hover:text-gold transition-colors"
          >
            <X size={22} />
          </button>
          <button
            data-testid="lightbox-prev"
            aria-label="Précédent"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-4 md:left-10 w-12 h-12 flex items-center justify-center border border-white/20 text-white hover:border-gold hover:text-gold transition-colors"
          >
            <ChevronLeft size={24} />
          </button>
          <button
            data-testid="lightbox-next"
            aria-label="Suivant"
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-4 md:right-10 w-12 h-12 flex items-center justify-center border border-white/20 text-white hover:border-gold hover:text-gold transition-colors"
          >
            <ChevronRight size={24} />
          </button>

          <motion.figure
            key={item.id || index}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-5xl w-full"
            onClick={(e) => e.stopPropagation()}
            onTouchStart={(e) => { e.currentTarget._touchX = e.touches[0].clientX; }}
            onTouchEnd={(e) => {
              const dx = e.changedTouches[0].clientX - (e.currentTarget._touchX || 0);
              if (Math.abs(dx) > 50) (dx > 0 ? prev : next)();
            }}
          >
            <div className="relative border border-white/10 bg-black">
              {video ? (
                video.kind === "mp4" ? (
                  <video data-testid="lightbox-video" src={video.src} controls playsInline preload="metadata" className="w-full max-h-[75vh]" />
                ) : (
                  <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
                    <iframe
                      data-testid="lightbox-video"
                      src={video.src}
                      title={title}
                      allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="absolute inset-0 w-full h-full"
                    />
                  </div>
                )
              ) : (
                <img src={item.url} alt={title} className="w-full max-h-[75vh] object-contain" />
              )}
            </div>
            <figcaption className="mt-4 flex items-baseline justify-between gap-4">
              <span className="text-white/80 text-sm">{title}</span>
              <span className="text-gold/70 text-xs font-bold tracking-[0.2em]">
                {String(index + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}
              </span>
            </figcaption>
          </motion.figure>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Lightbox;
